import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home,
  ShoppingBag,
  BookOpen,
  GraduationCap,
  Users,
  Tag,
  User,
  Menu,
  X,
  Search,
  Bell,
  ShoppingCart,
  MessageCircle,
  LogIn,
  LogOut,
  Plus,
  Flame,
  Settings,
  Rocket,
  Zap,
  Heart as HeartIcon,
  Brain,
  Lock,
  Radar,
  Shield,
  Beaker,
  ChevronDown,
  LayoutGrid,
  Sparkles,
  Store,
  Crown,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { OfficialOrEmployeeBadge } from '../shared/OfficialOrEmployeeBadge';
import { useCart } from '../../context/CartContext';
import { GlobalSearchOverlay } from '../search/GlobalSearchOverlay';
import { notificationService } from '../../services/supabaseService';
import { prefetchRoutePath } from '../../lib/routeChunkPreloads';

const warmRoute = (path: string) => () => prefetchRoutePath(path);

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const mainNavItems: NavItem[] = [
  { label: 'District', path: '/district', icon: <Home size={18} /> },
  { label: 'Connect', path: '/connect', icon: <Users size={18} /> },
  { label: 'Drops', path: '/drops', icon: <Flame size={18} /> },
  { label: 'Market', path: '/market', icon: <ShoppingBag size={18} /> },
  { label: 'Culture', path: '/culture', icon: <BookOpen size={18} /> },
  { label: 'Sell', path: '/sell', icon: <Tag size={18} /> },
];

const moreNavItems: NavItem[] = [
  { label: 'Stores', path: '/stores', icon: <Store size={18} /> },
  { label: 'Explore', path: '/explore', icon: <LayoutGrid size={18} /> },
  { label: 'Learn', path: '/learn', icon: <GraduationCap size={18} /> },
  { label: 'Collector', path: '/collector', icon: <Crown size={18} /> },
  { label: 'Vault', path: '/vault', icon: <Lock size={18} /> },
  { label: 'Heart', path: '/heart', icon: <HeartIcon size={18} /> },
  { label: 'Grow', path: '/grow', icon: <Rocket size={18} /> },
  { label: 'Nexus', path: '/nexus?m=impact', icon: <Zap size={18} /> },
];

export const Navigation: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isLoggedIn, signOut } = useAuth();
  const { itemCount } = useCart();

  const isActive = (path: string) => {
    if (path === '/connect') {
      return (
        location.pathname === '/connect' ||
        location.pathname.startsWith('/connect') ||
        location.pathname === '/community' ||
        location.pathname === '/clips'
      );
    }
    if (path === '/market') return location.pathname.startsWith('/market');
    if (path === '/explore') return location.pathname === '/explore';
    if (location.pathname === '/culture' || location.pathname.startsWith('/culture/')) {
      return path.startsWith('/culture');
    }
    return location.pathname === path;
  };

  // Load unread notification count
  useEffect(() => {
    if (!user) return;
    const loadCount = async () => {
      const { count } = await notificationService.getUnreadCount(user.id);
      setUnreadCount(count);
    };
    loadCount();
    const unsub = notificationService.subscribeToNew(user.id, () => {
      setUnreadCount(prev => prev + 1);
    });
    return unsub;
  }, [user]);

  // Close search and more dropdown on navigate
  useEffect(() => {
    setSearchOpen(false);
    setMoreOpen(false);
  }, [location.pathname]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/district');
  };

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-[100] bg-district-black/90 backdrop-blur-md border-b border-white/[0.06] pt-[env(safe-area-inset-top,0px)]">
        <div className="max-w-full mx-auto px-2 sm:px-6 lg:px-12">
          <div className="flex items-center justify-between h-14 lg:h-20">
            <Link to="/district" className="flex items-center shrink-0 press-effect" onMouseEnter={warmRoute('/district')} onFocus={warmRoute('/district')}>
              <span className="text-lg lg:text-xl font-semibold tracking-tight text-district-white">
                <span className="lg:hidden font-black">D<span className="text-district-red">88</span></span>
                <span className="hidden lg:inline">DISTRICT<span className="text-district-red font-semibold">88</span></span>
              </span>
            </Link>

            <div className="hidden lg:flex items-center gap-1 xl:gap-2 min-w-0 flex-1 justify-center max-w-4xl px-4">
              {mainNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-[13px] font-medium px-2.5 py-1.5 rounded-md transition-colors whitespace-nowrap ${isActive(item.path) ? 'text-district-white bg-white/[0.06]' : 'text-gray-500 hover:text-district-white'}`}
                >
                  {item.label}
                </Link>
              ))}
              <div className="relative">
                <button
                  onClick={() => setMoreOpen(!moreOpen)}
                  className={`flex items-center gap-1 text-[13px] font-medium px-2.5 py-1.5 rounded-md transition-colors ${moreNavItems.some(i => isActive(i.path)) ? 'text-district-white bg-white/[0.06]' : 'text-gray-500 hover:text-district-white'}`}
                >
                  More <ChevronDown size={14} className={`transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`} />
                </button>
                {moreOpen && (
                  <>
                    <div className="fixed inset-0 z-[100]" onClick={() => setMoreOpen(false)} aria-hidden="true" />
                    <div className="absolute top-full right-0 mt-1 py-2 w-[min(100vw-2rem,22rem)] bg-district-surface border border-neutral-800 rounded-lg shadow-2xl z-[110] grid grid-cols-1 sm:grid-cols-2 gap-0.5 p-1">
                      {moreNavItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setMoreOpen(false)}
                          onMouseEnter={warmRoute(item.path)}
                          onFocus={warmRoute(item.path)}
                          className={`flex items-center gap-2 px-3 py-2 text-[13px] rounded-md ${isActive(item.path) ? 'text-district-white bg-white/[0.06]' : 'text-gray-400 hover:bg-white/[0.04] hover:text-district-white'}`}
                        >
                          {item.icon} {item.label}
                        </Link>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-1 sm:space-x-2 lg:space-x-4">
              {/* Post Button */}
              {isLoggedIn && (
                <Link
                  to="/post/new"
                  onMouseEnter={warmRoute('/post/new')}
                  onFocus={warmRoute('/post/new')}
                  className="flex items-center justify-center h-10 w-10 sm:w-auto sm:h-auto sm:gap-1.5 sm:px-3.5 sm:py-2 border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] text-district-white text-sm font-semibold rounded-2xl transition-colors press-effect"
                >
                  <Plus size={18} className="sm:hidden" />
                  <span className="hidden sm:inline text-lg leading-none">+</span>
                  <span className="hidden lg:inline">Post</span>
                </Link>
              )}

              {/* Search */}
              <button onClick={() => setSearchOpen(!searchOpen)} className="h-10 w-10 flex items-center justify-center rounded-2xl text-gray-500 hover:text-district-white hover:bg-white/[0.06] transition-colors press-effect" title="Search">
                <Search size={20} />
              </button>

              {/* Notifications — mobile app icon */}
              <Link
                to="/notifications"
                onMouseEnter={warmRoute('/notifications')}
                onFocus={warmRoute('/notifications')}
                className="lg:hidden relative app-icon-btn text-gray-500 hover:text-district-white hover:bg-white/[0.06]"
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 bg-district-red rounded-full text-[10px] font-bold flex items-center justify-center text-white">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Link>

              {/* Notifications — desktop */}
              <Link to="/notifications" onMouseEnter={warmRoute('/notifications')} onFocus={warmRoute('/notifications')} className="hidden lg:flex p-2.5 min-w-[40px] min-h-[40px] items-center justify-center text-gray-500 hover:text-district-white transition-colors relative">
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-district-red rounded-full text-[10px] font-bold flex items-center justify-center text-white">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Link>

              {/* Messages — desktop only (mobile uses tab bar) */}
              <Link
                to="/messages"
                className="hidden lg:flex p-2.5 min-w-[40px] min-h-[40px] items-center justify-center text-gray-500 hover:text-district-white transition-colors relative"
              >
                <MessageCircle size={20} />
              </Link>

              {/* Cart */}
              <Link to="/cart" onMouseEnter={warmRoute('/cart')} onFocus={warmRoute('/cart')} className="relative h-10 w-10 flex items-center justify-center rounded-2xl text-gray-500 hover:text-district-white hover:bg-white/[0.06] transition-colors press-effect">
                <ShoppingCart size={20} />
                {itemCount > 0 && (
                  <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-district-red rounded-full text-[10px] font-bold flex items-center justify-center text-white">
                    {itemCount > 9 ? '9+' : itemCount}
                  </span>
                )}
              </Link>

              {/* Operations center — managers & staff (all breakpoints; was hidden below sm) */}
              {(user?.isEmployee || user?.isAdmin || user?.isManager) && (
                <Link
                  to="/operations"
                  onMouseEnter={warmRoute('/operations')}
                  onFocus={warmRoute('/operations')}
                  onTouchStart={warmRoute('/operations')}
                  className="flex p-2.5 min-w-[40px] min-h-[40px] items-center justify-center text-amber-400 hover:text-amber-300 transition-colors"
                  title="Operations center"
                  aria-label="Operations center"
                >
                  <Shield size={20} />
                </Link>
              )}

              {/* Explore grid — mobile app launcher */}
              <Link
                to="/explore"
                onMouseEnter={warmRoute('/explore')}
                onFocus={warmRoute('/explore')}
                className="lg:hidden app-icon-btn text-gray-500 hover:text-district-white hover:bg-white/[0.06]"
                aria-label="Explore District88"
              >
                <LayoutGrid size={20} />
              </Link>

              {/* Auth / Profile — desktop */}
              {isLoggedIn ? (
                <div className="hidden lg:flex items-center space-x-3">
                  <Link
                    to="/profile"
                    onMouseEnter={warmRoute('/profile')}
                    onFocus={warmRoute('/profile')}
                    className="flex items-center space-x-2 p-1.5 text-gray-400 hover:text-district-white transition-colors"
                  >
                    {user?.avatar ? (
                      <img src={user.avatar} alt={user.username} className="w-7 h-7 rounded-full object-cover border border-gray-700" />
                    ) : (
                      <User size={20} />
                    )}
                    <span className="flex items-center gap-1.5">
                      <span className="text-sm font-medium max-w-[100px] truncate">{user?.username}</span>
                      <OfficialOrEmployeeBadge
                        isOfficial={user?.isOfficial}
                        isEmployee={user?.isEmployee}
                        size="xs"
                      />
                    </span>
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                    title="Sign out"
                  >
                    <LogOut size={18} />
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                  className="hidden lg:flex items-center space-x-2 px-4 py-2 border border-white/20 hover:bg-white/[0.06] text-district-white rounded-lg text-sm font-medium transition-colors"
                >
                  <LogIn size={16} />
                  <span>Sign in</span>
                </Link>
              )}
            </div>
          </div>
        </div>

        <GlobalSearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
      </nav>

      {/* Mobile Menu — hidden; mobile uses tab bar + Explore launcher */}
      <AnimatePresence>
        {false && mobileMenuOpen && (
          <motion.div
            className="fixed inset-0 z-[55] flex flex-col justify-end lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <motion.div
              className="absolute inset-0 bg-black/55 backdrop-blur-[2px]"
              onClick={() => setMobileMenuOpen(false)}
              aria-hidden
            />
            <motion.nav
              role="dialog"
              aria-modal="true"
              aria-label="Site menu"
              className="relative z-10 flex max-h-[min(88dvh,calc(100dvh-3.5rem))] w-full flex-col rounded-t-2xl border border-white/10 border-b-0 bg-[#0f0f0f] shadow-[0_-16px_48px_rgba(0,0,0,0.55)]"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 32, stiffness: 380 }}
            >
              <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-white/20" aria-hidden />
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] space-y-1">
                {[...mainNavItems, ...moreNavItems].map((item, index) => (
                  <motion.div
                    key={item.path}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: Math.min(index * 0.03, 0.3) }}
                  >
                    <Link
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      onTouchStart={warmRoute(item.path)}
                      className={`flex items-center gap-3 px-4 py-3.5 text-[15px] font-semibold tracking-tight rounded-2xl min-h-[48px] press-effect
                        ${isActive(item.path)
                          ? 'text-district-white bg-white/[0.08] ring-1 ring-white/10'
                          : 'text-gray-400 hover:text-district-white hover:bg-white/[0.05]'
                        } transition-colors duration-200`}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </Link>
                  </motion.div>
                ))}

                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.3 }}
                  className="pt-4 mt-4 border-t border-gray-800"
                >
                  {isLoggedIn ? (
                    <>
                    <Link
                      to="/post/new"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center space-x-4 px-4 py-3.5 min-h-[48px] text-base font-semibold text-red-400 hover:text-red-300 hover:bg-gray-900/80 rounded-2xl transition-colors press-effect"
                    >
                      <Plus size={18} />
                      <span>Create Post</span>
                    </Link>
                      {(user?.isEmployee || user?.isAdmin || user?.isManager) && (
                        <Link
                          to="/operations"
                          onClick={() => setMobileMenuOpen(false)}
                          className="flex items-center space-x-4 px-4 py-3.5 min-h-[48px] text-base font-semibold text-amber-400 hover:text-amber-300 hover:bg-gray-900/80 rounded-2xl transition-colors press-effect"
                        >
                          <Shield size={18} />
                          <span>Operations center</span>
                        </Link>
                      )}
                      <Link
                        to="/notifications"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center space-x-4 px-4 py-3.5 min-h-[48px] text-base font-medium text-gray-200 hover:text-white hover:bg-gray-900/80 rounded-2xl transition-colors press-effect"
                      >
                        <Bell size={18} />
                        <span>Notifications</span>
                      </Link>
                      <Link
                        to="/messages"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center space-x-4 px-4 py-3.5 min-h-[48px] text-base font-medium text-gray-200 hover:text-white hover:bg-gray-900/80 rounded-2xl transition-colors press-effect"
                      >
                        <MessageCircle size={18} />
                        <span>Messages</span>
                      </Link>
                      <Link
                        to="/profile"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center space-x-4 px-4 py-3.5 min-h-[48px] text-base font-medium text-gray-200 hover:text-white hover:bg-gray-900/80 rounded-2xl transition-colors press-effect"
                      >
                        {user?.avatar ? (
                          <img src={user?.avatar} alt="" className="w-6 h-6 rounded-full object-cover" />
                        ) : (
                          <User size={18} />
                        )}
                        <span>{user?.username || 'Profile'}</span>
                      </Link>
                      <Link
                        to="/orders"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center space-x-4 px-4 py-3.5 min-h-[48px] text-base font-medium text-gray-200 hover:text-white hover:bg-gray-900/80 rounded-2xl transition-colors press-effect"
                      >
                        <ShoppingBag size={18} />
                        <span>My Orders</span>
                      </Link>
                      <Link
                        to="/settings"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center space-x-4 px-4 py-3.5 min-h-[48px] text-base font-medium text-gray-200 hover:text-white hover:bg-gray-900/80 rounded-2xl transition-colors press-effect"
                      >
                        <Settings size={18} />
                        <span>Settings</span>
                      </Link>
                      <button
                        type="button"
                        onClick={() => { handleSignOut(); setMobileMenuOpen(false); }}
                        className="flex items-center space-x-4 px-4 py-3.5 min-h-[48px] text-base font-medium text-red-400 hover:text-red-300 hover:bg-gray-900/80 rounded-2xl w-full text-left transition-colors press-effect"
                      >
                        <LogOut size={18} />
                        <span>Sign Out</span>
                      </button>
                    </>
                  ) : (
                    <Link
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center space-x-4 px-4 py-3.5 min-h-[48px] text-base font-medium text-district-red hover:text-red-300 hover:bg-gray-900/80 rounded-2xl transition-colors press-effect"
                    >
                      <LogIn size={18} />
                      <span>Sign In</span>
                    </Link>
                  )}
                </motion.div>
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
